import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Button, Column, Columns, TextInput } from '@resurs/midas-react'
import { useLocaleState } from '@merchant-portal/merchant-portal-base'
import { getDefaultVatRate } from '../../../../../models/countries'
import styles from './order.style'
import { getText } from '../../../../../internal/TextLabels'
import OrderLine from './OrderLine'
import OrderCaptions from './OrderCaptions'
import OrderSummary from './OrderSummary'
import { useOnUpdate } from '../../../../../util/utility-hooks'
import { getDeepOr } from '../../../../../util/helpers'

const buildOrderLines = (nbrOrderLines, countryCode) => {
  const orderLines = []
  for (let i = 0; i < nbrOrderLines; i += 1) {
    orderLines.push({
      articleNbr: '',
      description: '',
      quantity: null,
      unitPriceExVat: null,
      vat: 100 * getDefaultVatRate(countryCode),
      sum: null,
      key: i,
    })
  }
  return orderLines
}

const getTotalAmount = orderLines =>
  orderLines.reduce(
    (accumulator, orderLine) => accumulator + Number(orderLine.sum),
    0,
  )

const getTotalExVat = orderLines =>
  orderLines.reduce(
    (accumulator, orderLine) =>
      accumulator +
      Number(orderLine.unitPriceExVat) * Number(orderLine.quantity),
    0,
  )

const Order = props => {
  const context = useLocaleState()
  const orderIdTextInput = React.createRef()

  const [orderLines, setOrderLines] = useState(
    getDeepOr(
      buildOrderLines(3, context.country),
      props.data,
      'payment.order.orderLines',
    ),
  )
  const [totalAmount, setTotalAmount] = useState(
    getDeepOr(0, props.data, 'payment.order.totalAmount'),
  )
  const [totalExVat, setTotalExVat] = useState(0)
  const [orderId, setOrderId] = useState(
    getDeepOr('', props.data, 'payment.order.orderId'),
  )
  const [orderIdValid, setOrderIdValid] = useState(false)
  const [metaData, setMetaData] = useState({})

  useOnUpdate(() => {
    if (orderIdTextInput && orderIdTextInput.current) {
      orderIdTextInput.current.test({
        target: { value: orderId },
      })
    }
    setOrderIdValid(props.orderIdValid)
  }, [props.orderIdValid])

  const onAddNewRow = () =>
    setOrderLines(_orderLines =>
      _orderLines.concat({
        articleNbr: '',
        description: '',
        quantity: null,
        unitPriceExVat: null,
        vat: 100 * getDefaultVatRate(context.country),
        sum: null,
        key: orderLines.length + 1,
      }),
    )

  const onOrderIdChanged = _orderId => {
    setOrderId(_orderId)
    props.onChange({
      orderLines,
      totalAmount,
      totalExVat,
      orderId,
      orderIdValid,
      metaData,
    })
  }

  const onExternalRefSet = externalRef => {
    setMetaData({ ...metaData, externalRef })
    props.onChange({
      orderLines,
      totalAmount,
      totalExVat,
      orderId,
      orderIdValid,
      metaData,
    })
  }

  const onLineChanged = (lineInfo, index) => {
    const _orderLines = JSON.parse(JSON.stringify(orderLines))
    _orderLines[index].articleNbr = lineInfo.articleNbr
    _orderLines[index].description = lineInfo.description
    _orderLines[index].quantity = lineInfo.quantity
    _orderLines[index].unitPriceExVat = lineInfo.unitPriceExVat
    _orderLines[index].vat = lineInfo.vat
    _orderLines[index].sum = lineInfo.sum

    setTotalAmount(getTotalAmount(_orderLines))
    setTotalExVat(getTotalExVat(_orderLines))
    setOrderLines(_orderLines)
  }

  useEffect(() => {
    props.onChange({
      orderLines,
      totalAmount,
      totalExVat,
      orderId,
      orderIdValid,
      metaData,
    })
  }, [orderLines])

  return (
    <div>
      <Columns>
        <Column col6>
          <TextInput
            id="orderId"
            ref={orderIdTextInput}
            label={
              props.invoiceRef
                ? getText('payApply.order.orderId')
                : getText('payApply.order.invoiceRef')
            }
            additionalValidation={props.orderIdValidation}
            skipValidation
            onChange={e => onOrderIdChanged(e.target.value)}
            type="text"
            value={orderId}
          />
        </Column>
        <Column col6>
          <TextInput
            id="externalRef"
            label={getText('payApply.order.externalRef')}
            skipValidation
            onChange={e => onExternalRefSet(e.target.value)}
            type="text"
            value={metaData.externalRef || ''}
          />
        </Column>
        <Column col12 />
      </Columns>
      <OrderCaptions />
      {orderLines.map((line, index) => (
        <OrderLine
          key={line.key}
          line={line}
          onChange={lineInfo => onLineChanged(lineInfo, index)}
        />
      ))}
      <div style={styles.addRowBtn}>
        <Button
          text={getText('payApply.order.newRow')}
          onClick={() => onAddNewRow()}
          size="small"
        />
      </div>
      <div style={{ paddingTop: '2rem', fontStyle: 'italic', color: 'grey' }}>
        <small>
          {getText(
            'payApply.order.invoiceAmount',
            props.selectedPaymentMethod.minLimit,
            props.selectedPaymentMethod.maxLimit,
          )}
        </small>
      </div>
      <OrderSummary totalAmount={totalAmount} />
    </div>
  )
}

Order.propTypes = {
  data: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  invoiceRef: PropTypes.bool.isRequired,
  selectedPaymentMethod: PropTypes.object.isRequired,
  orderIdValidation: PropTypes.func.isRequired,
  orderIdValid: PropTypes.bool,
}

Order.defaultProps = {
  orderIdValid: true,
}

export default Order
